import {
    type AllSideSheets,
    type NavigationScreenState,
    transformNavigationScreen,
} from '@gitbook/app-navigation';

import type { NavigationState, NavigationTarget } from './types';

/**
 * Get the location for target operation.
 */
export function getNavigationLocationFor(
    currentState: NavigationState,
    to: NavigationTarget,
    stack: NavigationState[]
):
    | undefined
    | {
          /** New location to push/replace to the history */
          location: NavigationScreenState;
          background: NavigationScreenState | undefined;
          /** Relative movement in the history */
          go: number;
      } {
    // Open a screen on the overlay
    // put the current screen as a background.
    if ('overlay' in to && to.overlay !== null && to.overlay !== undefined) {
        const overlay = transformNavigationScreen(
            currentState.overlay || currentState.screen,
            to.overlay
        );

        // Preserve current background if we already have an overlay opened
        const background =
            'screen' in to
                ? transformNavigationScreen(currentState.screen, to.screen)
                : currentState.screen;
        return {
            location: overlay,
            background,
            go: 0,
        };
    }

    // Close the overlay
    // And navigate to background screen or defaults to "screen"
    if ('overlay' in to && to.overlay === null) {
        const currentOverlay = currentState.overlay;

        if (!currentOverlay) {
            // we only want to use the to.screen if there wasn't
            // any background screen (for example if the user
            // refreshed while seeing the overlay)
            const previousScreen: NavigationScreenState = { type: 'home' };
            return {
                location:
                    'screen' in to && to.screen
                        ? transformNavigationScreen(previousScreen, to.screen)
                        : previousScreen,
                background: undefined,
                go: 0,
            };
        }

        if ('screen' in to && to.screen) {
            const go = rewindHistory(stack, (state) => {
                return Boolean(state.overlay);
            });

            return {
                location: transformNavigationScreen(currentState.screen, to.screen),
                background: undefined,
                go,
            };
        }

        // Find the last screen that is not an overlay
        // we will go back to the point where we opened
        // the overlay instead of navigating further
        const go = rewindHistory(stack, (state) => {
            return Boolean(state.overlay);
        });

        return {
            location: currentState.screen,
            background: undefined,
            go,
        };
    }

    // Return to the previous app screen (settings "Back to app").
    // Settings can occupy either slot: the full-screen page sits in `screen`,
    // while billing/upgrade/invite entry points open it as an `overlay`. Walk
    // back to the most recent state that has an app view and return that,
    // pruning the settings entries from history.
    if ('back' in to && to.back === 'app') {
        for (let index = stack.length - 1; index >= 0; index--) {
            const appView = appViewOf(stack[index]);
            if (appView) {
                // `go` prunes the settings entries from history; the push that
                // follows re-establishes `location` as the current entry.
                const go = rewindHistory(stack, (state) => appViewOf(state) === null);
                return { ...appView, go };
            }
        }

        // No app screen to return to (direct link / reload): fall back to `home`.
        return {
            location: transformNavigationScreen({ type: 'home' }, to.screen),
            background: undefined,
            go: 0,
        };
    }

    // Navigate the entire screen, replacing overlay/background
    if ('screen' in to) {
        const newScreen = transformNavigationScreen(currentState.screen, to.screen);

        const hadOverlay = hasInScreenOverlay(currentState.screen);
        const hasOverlay = hasInScreenOverlay(newScreen);
        let go = 0;

        if (hadOverlay && !hasOverlay) {
            // Closing the overlay, we go back to the last screen that is not an overlay
            go = rewindHistory(stack, (state) => {
                return hasInScreenOverlay(state.screen);
            });
        }

        return {
            location: newScreen,
            background: undefined,
            go,
        };
    }

    // Update background screen
    if ('background' in to) {
        if (!currentState.overlay) {
            // If we are not in an overlay, we don't have a background to update
            return;
        }

        return {
            location: currentState.overlay,
            background: transformNavigationScreen(currentState.screen, to.background),
            go: 0,
        };
    }

    // Update current screen (it could be overlay or main screen)
    if ('current' in to) {
        return {
            location: transformNavigationScreen(
                currentState.overlay || currentState.screen,
                to.current
            ),
            background: currentState.overlay ? currentState.screen : undefined,
            go: 0,
        };
    }
}

/** The side sheet open on a screen, if any. */
function sideSheetOfScreen(screen: NavigationScreenState): AllSideSheets | undefined {
    return 'sideSheet' in screen && screen.sideSheet ? screen.sideSheet : undefined;
}

/**
 * The side sheet showing on a state's foreground screen — the overlay when there is one, since
 * that is what the reader is looking at.
 */
function sideSheetOf(state: NavigationState): AllSideSheets | undefined {
    return sideSheetOfScreen(state.overlay ?? state.screen);
}

/**
 * What a state is about, as far as going back between its side sheets is concerned. A reader
 * moving inside a panel changes the page or the thread, and that must not end the search; moving
 * to another change request must.
 *
 * `space`, `changeRequest` and `organization` are the ones that decide anything today. `site` is
 * defensive, against a state pushed for a different site. `collection` can never decide: a
 * collection has one side sheet type, so two of its entries match each other and the walk passes
 * over them before it ever asks what they are about.
 */
const SUBJECT_KEYS = ['space', 'changeRequest', 'collection', 'site', 'organization'] as const;

/** Whether two states are showing the same subject, so one's side sheet sits behind the other's. */
function isSameSubject(a: NavigationState, b: NavigationState): boolean {
    const screenA = a.overlay ?? a.screen;
    const screenB = b.overlay ?? b.screen;
    if (screenA.type !== screenB.type) {
        return false;
    }
    return SUBJECT_KEYS.every((key) => subjectKey(screenA, key) === subjectKey(screenB, key));
}

/**
 * One subject key of a screen, as a string. A change request is a number in a state built in the
 * app and a string in one parsed back from a URL, and both mean the same change request.
 */
function subjectKey(screen: NavigationScreenState, key: (typeof SUBJECT_KEYS)[number]): string {
    const value = (screen as Record<string, unknown>)[key];
    return value === undefined ? '' : String(value);
}

/**
 * The side sheet a panel's "Back" returns to: the one most recently open in history, on the same
 * subject, that is not the one open now.
 *
 * Entries showing the same side sheet are steps taken inside it — a thread opened, a page changed
 * — and are passed over, so back leaves the panel rather than retracing it. The search stops at
 * the first entry with no side sheet at all: that is the panel having been closed and reopened,
 * not one panel sitting behind another, and there is nothing to go back to.
 *
 * Returned whole rather than as its type, so a panel with tabs is restored as the reader left it.
 */
export function findSideSheetToGoBackTo(
    currentState: NavigationState,
    stack: NavigationState[]
): AllSideSheets | null {
    const current = sideSheetOf(currentState);
    if (!current) {
        return null;
    }

    for (let index = stack.length - 1; index >= 0; index--) {
        const state = stack[index];
        if (!isSameSubject(state, currentState)) {
            return null;
        }
        const sideSheet = sideSheetOf(state);
        if (!sideSheet) {
            return null;
        }
        if (sideSheet.type !== current.type) {
            return sideSheet;
        }
    }

    return null;
}

/**
 * Test if a screen has an inline overlay (side-sheet) opened.
 */
function hasInScreenOverlay(screen: NavigationScreenState) {
    return Boolean(sideSheetOfScreen(screen));
}

/**
 * Test if a screen is one of the settings screens.
 */
function isSettingsScreen(screen: NavigationScreenState): boolean {
    return screen.type === 'account' || screen.type === 'org_settings';
}

/**
 * Return the app-facing view of a navigation state for the "Back to app" target,
 * or `null` when the state is entirely settings (nothing to return to).
 *
 * Settings can sit in either slot, so this looks past a settings overlay to the
 * app content behind it:
 * - an app overlay (e.g. a change request) is returned with its background;
 * - otherwise the app screen is returned, dropping any settings overlay on top;
 * - a state whose screen and foreground are both settings yields `null`.
 */
function appViewOf(
    state: NavigationState
): { location: NavigationScreenState; background: NavigationScreenState | undefined } | null {
    if (state.overlay && !isSettingsScreen(state.overlay)) {
        return { location: state.overlay, background: state.screen };
    }
    if (!isSettingsScreen(state.screen)) {
        return { location: state.screen, background: undefined };
    }
    return null;
}

/**
 * Return the movement in the history to get to the latest state prior to where `predictate` returns `true`.
 * Calling `history.go(go)` will bring you to replace the state where `predictate` returns `true`.
 */
function rewindHistory(
    stack: NavigationState[],
    predicate: (state: NavigationState) => boolean
): number {
    if (stack.length === 0) {
        return 0;
    }

    let go = 0;
    for (let index = stack.length - 1; index >= 0; index--) {
        go = index - stack.length;
        if (!predicate(stack[index])) {
            break;
        }
    }

    // Ensure we can't go back further than the first state
    // to avoid hard-refresh
    go = Math.max(go, -(stack.length - 1));

    return go;
}
